/**
 * Post-login route guard — authenticated users must not land on marketing/auth
 * entry URLs via back/forward or stale history entries.
 */
import { isPaymentFlowInProgress, isPaymentReturnPath } from '../../utils/paymentReturnBootstrap';
import type { AppView } from '../store/AppStateManager';
import { POST_AUTH_LANDING_PATH, resolvePostAuthRedirect } from './postAuthLanding';
import { resolveAppViewFromPath } from './routeRegistry';

const POST_AUTH_BLOCKED_EXACT = new Set(['/', '/login', '/signup']);

export interface PostAuthRouteGuardResult {
  path: string;
  view: AppView;
}

/** Synchronous guard — call on init and popstate before rendering. */
export function enforcePostAuthRouteGuard(hasSession: boolean): PostAuthRouteGuardResult | null {
  if (typeof window === 'undefined' || !hasSession) return null;

  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const search = window.location.search;

  if (path === '/payment-success' || path === '/payment-failed' || path === '/checkout') {
    return null;
  }
  if (path === '/signup/consent') return null;
  if (isPaymentReturnPath(path, search) || isPaymentFlowInProgress()) return null;
  if (!POST_AUTH_BLOCKED_EXACT.has(path)) return null;

  const stored = sessionStorage.getItem('redirect_after_login');
  if (stored) sessionStorage.removeItem('redirect_after_login');
  const targetPath = resolvePostAuthRedirect(stored) || POST_AUTH_LANDING_PATH;

  if (window.location.pathname !== targetPath) {
    window.history.replaceState({}, '', targetPath);
  }

  const resolved = resolveAppViewFromPath(targetPath);
  return { path: targetPath, view: resolved.view };
}
